<template>
  <b-card
    tag="article"
    class="m-2 content-wrapper "
  >
    <div
      v-if="isFetching"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="Loading..."
      />
    </div>
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h3 class="font-weight-bolder text-white mb-0">
          Activity Details
        </h3>
        <b-badge
          variant="light-primary"
          class="px-2 py-1"
        >
          <span class="font-medium-1">Activity Code: {{ activity_code }}</span>
        </b-badge>
      </div>

      <div class="info-grid mb-2">
        <div class="info-item">
          <span class="info-label">Experiment</span>
          <span class="info-value">{{ experimentName }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Course ID</span>
          <span class="info-value">{{ moodle_course_id }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Institution</span>
          <span class="info-value">{{ institutionName }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Created By</span>
          <span class="info-value">{{ creatorName }}</span>
        </div>
      </div>

      <div class="mt-3">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4 class="font-weight-bolder text-white mb-0">
            Participants
          </h4>
          <b-badge
            pill
            variant="light-secondary"
          >
            Total: {{ selectedStudents.length }}
          </b-badge>
        </div>

        <b-table
          :items="selectedStudents"
          :fields="fields"
          responsive
          show-empty
          empty-text="No students assigned"
          class="mb-0"
          striped
        >
          <template #cell(name)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                :src="data.item.avatar"
                :text="avatarText(data.item.name)"
                variant="light-primary"
                class="mr-1"
              />
              <div>
                <b-link
                  v-if="data.item.id"
                  :to="{ name: 'pages_profile', params: { user_id: data.item.id } }"
                  class="font-weight-bold d-block text-primary"
                >
                  {{ data.item.name }}
                </b-link>
                <span
                  v-else
                  class="font-weight-bold d-block text-white"
                >{{
                  data.item.name
                }}</span>
              </div>
            </div>
          </template>
          <template #cell(progress)="data">
            <div class="d-flex align-items-center">
              <div
                class="position-relative d-flex align-items-center justify-content-center mr-1"
              >
                <svg
                  class="progress-ring"
                  width="50"
                  height="50"
                >
                  <circle
                    class="progress-ring__circle-bg"
                    stroke="rgba(255,255,255,0.1)"
                    stroke-width="4"
                    fill="transparent"
                    r="20"
                    cx="25"
                    cy="25"
                  />
                  <circle
                    class="progress-ring__circle"
                    :stroke="data.item.progress === 100 ? '#28c76f' : '#7367f0'"
                    stroke-width="4"
                    fill="transparent"
                    r="20"
                    cx="25"
                    cy="25"
                    :style="{
                      strokeDashoffset: getCircleDashOffset(
                        data.item.progress,
                        20
                      ),
                    }"
                  />
                </svg>
                <span
                  class="font-small-2 font-weight-bold position-absolute"
                  :class="
                    data.item.progress === 100 ? 'text-success' : 'text-primary'
                  "
                >
                  {{ data.item.progress }}%
                </span>
              </div>
            </div>
          </template>
          <template #cell(actions)="data">
            <b-link
              :tooltip="'none'"
              class="text-info"
              title="View Progress Log"
              @click="viewStudentProgress(data.item)"
            >
              <feather-icon
                icon="FileTextIcon"
                size="25"
              />
            </b-link>

            <feather-icon
              v-if="!syncingIds.includes(data.item.id)"
              v-b-tooltip.hover.top="'Sync Activity'"
              icon="RefreshCwIcon"
              size="20"
              class="text-info cursor-pointer ml-1"
              @click="syncActivity(data.item.id)"
            />
            <feather-icon
              v-else
              v-b-tooltip.hover.top="'Syncing...'"
              icon="LoaderIcon"
              size="20"
              class="text-info cursor-pointer ml-1"
            />

          </template>
        </b-table>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BSpinner,
  BBadge,
  BTable,
  BAvatar,
  BCardTitle,
  BCardText,
  BButton,
  BLink,
  VBTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import { avatarText } from '@core/utils/filter'
import { FileTextIcon } from 'vue-feather-icons'

export default {
  name: 'ViewActivity',
  components: {
    BCard,
    BRow,
    BCol,
    BSpinner,
    BBadge,
    BTable,
    BAvatar,
    BCardTitle,
    BCardText,
    BButton,
    BLink,
    FileTextIcon,
    VBTooltip,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      current_experiment_token_id: null,
      syncingIds: [],
      activity_code: '',
      experimentName: '',
      moodle_course_id: '',
      institutionName: '',
      creatorName: '',
      selectedStudents: [],
      isFetching: false,
      fields: [
        { key: 'name', label: 'Student', thClass: 'text-white' },
        { key: 'email', label: 'Email', thClass: 'text-white' },
        { key: 'progress', label: 'Progress', thClass: 'text-white' },
        {
          key: 'actions',
          label: 'Actions',
          thClass: 'text-white',
        },
      ],
      activityId: null,
      experimentId: null,
    }
  },
  computed: {
    ...mapGetters({
      experimentsLookups: 'experiments/experimentsLookups',
      loadSyncActivity: 'activities/loadSyncActivity',

    }),

  },
  created() {
    this.activityId = this.$route.params.id
    this.fetchActivityDetails()
  },
  methods: {
    avatarText,
    syncActivity(experiment_token_id) {
      this.current_experiment_token_id = experiment_token_id
      this.syncingIds.push(experiment_token_id)
      this.$store.dispatch('activities/syncActivity', { id: this.activityId, query: { experiment_token_id } }).then(response => {
        // console.log("response", response);
        this.current_experiment_token_id = null
        this.syncingIds = this.syncingIds.filter(id => id !== experiment_token_id)
        this.$swal({
          icon: 'success',
          title: 'Activity synced successfully',
          showConfirmButton: true,
          confirmButtonColor: '#7367f0',
        })
      }).catch(error => {
        console.error(error)
        this.current_experiment_token_id = null
        this.syncingIds = this.syncingIds.filter(id => id !== experiment_token_id)
        this.$swal({
          icon: 'error',
          title: 'Failed to sync activity',
          showConfirmButton: true,
          confirmButtonColor: '#7367f0',
        })
      })
    },
    getCircleDashOffset(percentage, radius) {
      const circumference = 2 * Math.PI * radius
      return circumference - (percentage / 100) * circumference
    },
    fetchActivityDetails() {
      this.isFetching = true
      this.$store
        .dispatch('activities/get', this.activityId)
        .then(response => {
          const activity = response.data
          this.activity_code = activity.activity_code

          // Experiment Name
          let exp = activity.experiment_id
          if (Array.isArray(exp)) exp = exp[0]
          if (Array.isArray(exp)) exp = exp[0]
          this.experimentName = activity.experiment
            ? activity.experiment.name
            : exp && typeof exp === 'object'
              ? exp.name
              : 'Unknown Experiment'

          this.moodle_course_id = activity.moodle_course_id
          this.institutionName = activity.institution
            ? activity.institution.name
            : 'N/A'
          this.creatorName = activity.creator
            ? activity.creator.full_name
            : 'N/A'

          // Set Experiment ID
          if (activity.experiment) {
            this.experimentId = activity.experiment.id
          } else if (exp && typeof exp === 'object') {
            this.experimentId = exp.id
          } else {
            this.experimentId = exp
          }

          if (activity.users) {
            this.selectedStudents = activity.users.map(user => ({
              ...user,
              name: user.full_name,
              progress: user.progress ? parseFloat(user.progress) : 0,
            }))
          }
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    viewStudentProgress(student) {
      this.$swal({
        title: 'Loading progress log...',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          this.$swal.showLoading()
        },
      })

      this.$store
        .dispatch('experiments/getStudentProgressLog', {
          user_id: student.id,
          experiment_id: this.experimentId,
        })
        .then(res => {
          // Handle direct array (unwrapped by interceptor) or Laravel resource wrapper
          let logs = []
          if (Array.isArray(res)) {
            logs = res
          } else if (res.data && Array.isArray(res.data)) {
            logs = res.data
          } else if (
            res.data
            && res.data.data
            && Array.isArray(res.data.data)
          ) {
            logs = res.data.data
          }

          let logsHtml = ''
          if (logs.length > 0) {
            logsHtml = '<div class="versions-container">'
            logs.forEach(log => {
              const date = log.created_at
                ? new Date(log.created_at).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
                : 'N/A'

              logsHtml += `
              <div class="version-item">
                <div class="d-flex align-items-center">
                  <span class="badge badge-light-primary font-weight-bold mr-1">
                    ${parseFloat(log.progress)}%
                  </span>
                  <span class="text-white small">
                    ${log.duration_formatted || '00:00:00'}
                  </span>
                </div>
                <div class="text-muted small">
                  ${date}
                </div>
              </div>
            `
            })
            logsHtml += '</div>'
          } else {
            logsHtml = `
            <div class="text-center py-4 text-muted">
              <p class="mb-0">No progress logs found</p>
            </div>
          `
          }

          this.$swal({
            title: `<strong>Progress Log - ${student.name}</strong>`,
            html: logsHtml,
            width: '600px',
            showCloseButton: true,
            showConfirmButton: false,
            customClass: {
              popup: 'versions-swal-popup',
              htmlContainer: 'versions-swal-content',
            },
            background: '#161d31', // Dark theme background
            color: '#fff', // Dark theme text color
          })
        })
        .catch(error => {
          console.error(error)
          this.$swal({
            icon: 'error',
            title: 'Failed to load progress log',
            text: error.response?.data?.message || 'An error occurred',
            confirmButtonColor: '#7367f0',
          })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.card {
  font-family: "Inter", sans-serif;
}

.content-wrapper {
  background: #000a1e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  color: #e9ecef;
  animation: slideInLeft 0.5s ease forwards;

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.info-item {
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 10px;
  border-left: 4px solid rgba(15, 217, 222, 0.5);
  transition: transform 0.3s ease, border-color 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-left-color: rgba(133, 13, 204, 1);
    background: rgba(255, 255, 255, 0.08);
  }
}

.info-label {
  display: block;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  display: block;
  font-size: 1.5rem;
  color: #fff;
  font-weight: 500;
  word-break: break-word;
}

.progress-ring__circle {
  stroke-dasharray: 125.66; /* 2 * PI * 20 */
  transition: stroke-dashoffset 0.35s;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}
</style>

<style lang="scss">
.versions-swal-popup {
  .swal2-close {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    color: #fff !important;

    &:focus {
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
    }
  }

  .versions-container {
    max-height: 400px;
    overflow-y: auto;
    text-align: left;

    .version-item {
      padding: 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      transition: background-color 0.2s;

      &:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }

      &:last-child {
        border-bottom: none;
      }
    }
  }
}
</style>
